import type { ReactNode } from 'react';
import type { LearningsScreenManager, LearningsTab } from '@/state/learnings/useLearningsScreen';
import { ChevronRight, Compass, Grid3x3, Lightbulb } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from 'helpers/cn';
import { FOCUS_RING } from '../../ConsolePanel';

type ViewTabsProps = {
  screen: LearningsScreenManager;
  opportunities: number;
};

type ViewTabProps = { value: LearningsTab; icon: ReactNode; label: ReactNode; onReopen: () => void };

/**
 * One tab, styled as the experiment page's (Experiments/Details/index.tsx ScreenTab): mono
 * micro-label, the active state from globals.css. `onReopen` takes the reader back to the list when
 * the tab is already theirs but they are on a pattern or a debrief under it: Radix fires no change
 * for the active tab.
 */
const ViewTab = ({ value, icon, label, onReopen }: ViewTabProps) => (
  <TabsTrigger
    value={value}
    onClick={onReopen}
    className="gap-1.5 rounded-lg px-3 py-1.5 font-mono text-nano font-medium tracking-[0.1em] uppercase shadow-none transition-colors duration-150 data-[state=active]:shadow-none"
  >
    {icon}
    {label}
  </TabsTrigger>
);

const ICON = 'size-3.5';

/**
 * The three views as the experiment page's tabs (decided 2026-10-20, after a dropdown in the crumb bar
 * proved easy to miss), at the left of the row above every view. On a pattern or a debrief the tab
 * it was opened from stays selected, and the trail under the row (ViewTrail) says where in it the
 * reader is.
 */
export const ViewTabs = ({ screen, opportunities }: ViewTabsProps) => {
  const { view, section } = screen;
  const below = view.screen !== 'tab';
  const reopen = (value: LearningsTab) => () => {
    if (below && value === section) screen.openTab(value);
  };
  return (
    <Tabs value={section} onValueChange={(value) => screen.openTab(value as LearningsTab)}>
      <TabsList
        aria-label="Learnings views"
        data-tour="learnings-tabs"
        className="border-line bg-panel h-auto w-fit gap-1 rounded-lg border p-1"
      >
        <ViewTab
          value="learnings"
          icon={<Lightbulb className={ICON} strokeWidth={1.5} />}
          label="Learnings"
          onReopen={reopen('learnings')}
        />
        <ViewTab
          value="evidence"
          icon={<Grid3x3 className={ICON} strokeWidth={1.5} />}
          label="Evidence"
          onReopen={reopen('evidence')}
        />
        <ViewTab
          value="opportunities"
          icon={<Compass className={ICON} strokeWidth={1.5} />}
          label={
            <>
              Opportunities<span className="text-warn">{opportunities}</span>
            </>
          }
          onReopen={reopen('opportunities')}
        />
      </TabsList>
    </Tabs>
  );
};

const LABELS: Record<LearningsTab, string> = {
  learnings: 'All learnings',
  evidence: 'All evidence',
  opportunities: 'All opportunities',
};

const STEP = 'text-dim hover:text-foreground min-w-0 truncate rounded px-1 text-meta transition-colors';

type ViewTrailProps = {
  screen: LearningsScreenManager;
  /** The label of the pattern on the trail, from the page's labels. */
  patternLabel: string | null;
};

/**
 * Where the reader is below a tab: "All learnings › Trust on checkout › #5871", each earlier step a
 * link back. Nothing on the tabs themselves, where the tab says it.
 */
export const ViewTrail = ({ screen, patternLabel }: ViewTrailProps) => {
  const { view, section } = screen;
  if (view.screen === 'tab') return null;
  const onTest = view.screen === 'test';
  return (
    <nav aria-label="Where you are">
      <ol className="m-0 flex min-w-0 list-none flex-wrap items-center gap-1 p-0">
        <li className="min-w-0">
          <button type="button" onClick={() => screen.openTab(section)} className={cn(STEP, FOCUS_RING)}>
            {LABELS[section]}
          </button>
        </li>
        {view.screen === 'foundation' && <Current>Foundation check</Current>}
        {patternLabel && screen.patternKey && (view.screen === 'pattern' || onTest) && (
          <>
            <Separator />
            {onTest ? (
              <li className="min-w-0">
                <button
                  type="button"
                  onClick={() => screen.openPattern(screen.patternKey)}
                  className={cn(STEP, FOCUS_RING)}
                >
                  {patternLabel}
                </button>
              </li>
            ) : (
              <CurrentStep>{patternLabel}</CurrentStep>
            )}
          </>
        )}
        {view.screen === 'test' && <Current>#{view.experimentId}</Current>}
      </ol>
    </nav>
  );
};

const Separator = () => (
  <li aria-hidden="true" className="text-dim">
    <ChevronRight className="size-3.5" strokeWidth={1.5} />
  </li>
);

type CurrentProps = { children: ReactNode };

const CurrentStep = ({ children }: CurrentProps) => (
  <li aria-current="page" className="min-w-0 truncate px-1 text-meta font-medium text-foreground">
    {children}
  </li>
);

/** The page the reader is on, after a separator. */
const Current = ({ children }: CurrentProps) => (
  <>
    <Separator />
    <CurrentStep>{children}</CurrentStep>
  </>
);
